import { useMemo } from 'react';
import { CSSObject, Global } from '@emotion/react';
import { Box } from '@gemini/core';
import { SelectCardGroup } from '@gemini/ui';
import { getMediaComponent } from '@mfe-helpers/fieldComponent';
import { useAppContext } from '@mfe-presentation/contexts';
import { IFieldProps, IFieldValues } from '@mfe-types/field';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';

const radioCardWrapperStyles: (nbOfItems: number) => CSSObject = (nbOfItems) => {
    const isEven = nbOfItems % 2 === 0;
    return {
        '.radio-card-wrapper': {
            label: { minWidth: '9.5rem', ...(isEven && { flex: '1 1 0' }) },
        },
        '@media screen and (max-width:370px)': {
            // odd case but applied for all cases for consistency
            '.radio-card-wrapper': {
                'div[role="radiogroup"]': {
                    flexDirection: 'column',
                },
            },
        },
    };
};

const RadioCardWrapper = ({ field, options, texts, styles }: IFieldProps<any, any>) => {
    const { onChange, ...fieldProps } = field;
    const { portal, useShellContext } = useAppContext();
    const shellContext = useShellContext();

    // avoid re-renders and re-fetching svg sprite
    const items: Array<any> = useMemo(() => {
        if (options?.values) {
            return options.values.map((option: IFieldValues) => ({
                description: option.additionalText,
                image: getMediaComponent(portal, option.icon, shellContext),
                label: option.label,
                value: option.value,
            }));
        }
        return [];
    }, []);

    const label = texts.label ?? {
        content: texts?.ariaLabel?.text ?? '',
        isHidden: true,
    };

    const handleChange = (value: string) => {
        onChange(value);
        if (!isFrenchPortal(portal)) {
            import(/* webpackChunkName 'lottie-web'*/ 'lottie-web').then(({ default: lottie }) => {
                const option = options.values?.find((item) => item.value === value);
                const name = option?.icon;
                if (name) {
                    lottie.stop(name);
                    lottie.play(name);
                }
            });
        }
    };

    return (
        <Box {...styles} unsafe_className="radio-card-wrapper" testId={field.name}>
            <Global styles={radioCardWrapperStyles(items.length)} />
            <SelectCardGroup
                helper={texts.additionalText}
                testId={field.name}
                // @ts-expect-error typescript conflict between FieldHiddenLabelProps | FieldDisplayedLabelProps
                label={label}
                onChange={handleChange}
                {...fieldProps}
                items={items}
            />
        </Box>
    );
};

export default RadioCardWrapper;
